import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { useLocationContext } from "@/contexts/LocationContext";
import { startOfMonth, endOfMonth } from "date-fns";
import { convertCurrencyAmount } from "@/lib/fx-rates";

interface DashboardStats {
  monthlyAppointments: number;
  monthlyRevenue: number; // Appointments revenue
  invoiceRevenue: number; // Invoice revenue
  walkInRevenue: number; // Walk-in/day pass revenue
  membershipRevenue: number; // Membership renewal revenue
  totalRevenue: number; // Total revenue (appointments + invoices + walk-ins + memberships)
  projectedRevenue: number;
  downpaymentsReceived: number;
  newCustomers: number;
  pendingAppointments: number;
  upcomingAppointments: {
    id: string;
    start_datetime: string;
    customer_name: string;
    service_name: string;
    status: string;
  }[];
}

export function useDashboardStats() {
  const { business } = useBusiness();
  const { selectedLocationId } = useLocationContext();
  const [stats, setStats] = useState<DashboardStats>({
    monthlyAppointments: 0,
    monthlyRevenue: 0,
    invoiceRevenue: 0,
    walkInRevenue: 0,
    membershipRevenue: 0,
    totalRevenue: 0,
    projectedRevenue: 0,
    downpaymentsReceived: 0,
    newCustomers: 0,
    pendingAppointments: 0,
    upcomingAppointments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!business) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const monthStart = startOfMonth(now).toISOString();
      const monthEnd = endOfMonth(now).toISOString();
      const displayCurrency = business.currency || "GHS";
      const convertRevenue = (amount: number, sourceCurrency?: string | null) =>
        convertCurrencyAmount(amount, sourceCurrency || displayCurrency, displayCurrency);

      // Fetch monthly appointments with details
      let appointmentsQuery = supabase
        .from("appointments")
        .select(`
          *,
          customer:customers(name),
          service:services(name, price_amount, currency)
        `)
        .eq("business_id", business.id)
        .gte("start_datetime", monthStart)
        .lte("start_datetime", monthEnd);
      
      // Filter by location if selected
      if (selectedLocationId) {
        appointmentsQuery = appointmentsQuery.eq("location_id", selectedLocationId);
      }
      
      const { data: appointments, error: appError } = await appointmentsQuery;

      if (appError) throw appError;

      // Fetch new customers this month
      const { data: newCustomersData, error: custError } = await supabase
        .from("customers")
        .select("id")
        .eq("business_id", business.id)
        .gte("created_at", monthStart)
        .lte("created_at", monthEnd);

      if (custError) throw custError;

      // Fetch upcoming appointments
      let upcomingQuery = supabase
        .from("appointments")
        .select(`
          id,
          start_datetime,
          status,
          customer:customers(name),
          service:services(name)
        `)
        .eq("business_id", business.id)
        .gte("start_datetime", now.toISOString())
        .in("status", ["confirmed", "pending"])
        .order("start_datetime", { ascending: true })
        .limit(5);
      
      // Filter by location if selected
      if (selectedLocationId) {
        upcomingQuery = upcomingQuery.eq("location_id", selectedLocationId);
      }
      
      const { data: upcoming, error: upcomingError } = await upcomingQuery;

      if (upcomingError) throw upcomingError;

      // Fetch downpayments + balance payments received this month (all appointment-related payments)
      // Includes: single deposit, group deposit, and balance-paid (so business Paystack or platform all show)
      const { data: downpayments, error: downpaymentsError } = await supabase
        .from("platform_payments")
        .select("gross_amount, currency, paid_at")
        .eq("business_id", business.id)
        .in("context", ["appointment_deposit", "appointment_group_deposit", "appointment_balance"])
        .eq("status", "success")
        .gte("paid_at", monthStart)
        .lte("paid_at", monthEnd);

      if (downpaymentsError) throw downpaymentsError;

      // Calculate stats
      const completedAppointments = appointments?.filter(
        (a) => a.status === "completed"
      ) || [];
      const confirmedAppointments = appointments?.filter(
        (a) => a.status === "confirmed"
      ) || [];
      const pendingCount = appointments?.filter(
        (a) => a.status === "pending"
      ).length || 0;

      const netAppointmentTotal = (a: any) => {
        const total = Number(a?.total_price_amount);
        if (Number.isFinite(total) && total > 0) return total;
        const servicePrice = Number(a?.service?.price_amount || 0);
        const discount = Number(a?.discount_amount || 0);
        return Math.max(0, servicePrice - discount);
      };
      const appointmentCurrency = (a: any) =>
        String(a?.currency || a?.service?.currency || displayCurrency);
      const displayAppointmentTotal = (a: any) => convertRevenue(netAppointmentTotal(a), appointmentCurrency(a));

      // Calculate downpayments received this month (for visibility only, NOT added to revenue)
      // Downpayments are deposits paid but revenue is only earned when service is completed
      const downpaymentsReceived = (downpayments || []).reduce(
        (sum, p: any) => sum + convertRevenue(Number(p.gross_amount || 0), p.currency),
        0
      );

      // Monthly revenue = completed appointments at FULL price only
      // When an appointment is completed, the full amount (including any deposit) is counted
      // We do NOT add downpayments separately to avoid double-counting
      const monthlyRevenue = completedAppointments.reduce(
        (sum, a: any) => sum + displayAppointmentTotal(a),
        0
      );
      
      // Projected revenue = confirmed appointments (full price) + completed appointments revenue
      // This shows potential revenue from confirmed bookings plus actual revenue earned
      const projectedRevenue = confirmedAppointments.reduce(
        (sum, a: any) => sum + displayAppointmentTotal(a),
        0
      ) + monthlyRevenue;

      // Fetch invoice revenue (paid invoices this month)
      const { data: invoices, error: invError } = await supabase
        .from("invoices")
        .select(`
          id,
          currency,
          invoice_items(id, quantity, unit_price),
          invoice_adjustments(id, type, mode, amount, percent_rate, included_in_prices)
        `)
        .eq("business_id", business.id)
        .eq("status", "paid")
        .gte("paid_at", monthStart)
        .lte("paid_at", monthEnd);

      if (invError) throw invError;

      // Calculate invoice revenue (items + adjustments)
      let invoiceRevenue = 0;
      if (invoices) {
        for (const inv of invoices) {
          // Sum line items
          const itemsTotal = (inv.invoice_items || []).reduce(
            (sum: number, item: any) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
            0
          );

          // Apply adjustments
          let adjustedTotal = itemsTotal;
          for (const adj of inv.invoice_adjustments || []) {
            if (adj.type === "tax" && adj.included_in_prices) {
              // Tax is already included, don't add it
              continue;
            }
            if (adj.mode === "percent") {
              const percentValue = Number(adj.percent_rate || 0);
              if (adj.type === "tax") {
                adjustedTotal += (itemsTotal * percentValue) / 100;
              } else if (adj.type === "discount") {
                adjustedTotal -= (itemsTotal * percentValue) / 100;
              }
            } else {
              // Fixed amount
              if (adj.type === "tax") {
                adjustedTotal += Number(adj.amount || 0);
              } else if (adj.type === "discount") {
                adjustedTotal -= Number(adj.amount || 0);
              }
            }
          }

          invoiceRevenue += convertRevenue(adjustedTotal, (inv as any).currency);
        }
      }

      // Fetch walk-in revenue (day passes this month)
      const { data: dayPasses, error: dpError } = await supabase
        .from("day_passes")
        .select("amount, currency, payment_status, created_at")
        .eq("business_id", business.id)
        .eq("payment_status", "paid")
        .gte("created_at", monthStart)
        .lte("created_at", monthEnd);

      if (dpError) throw dpError;

      const walkInRevenue = (dayPasses || []).reduce(
        (sum: number, dp: any) => sum + convertRevenue(Number(dp.amount || 0), dp.currency),
        0
      );

      // Fetch membership renewal revenue (membership_payment context this month)
      const { data: membershipPayments, error: mpError } = await supabase
        .from("platform_payments")
        .select("gross_amount, currency, paid_at")
        .eq("business_id", business.id)
        .eq("context", "membership_payment")
        .eq("status", "success")
        .gte("paid_at", monthStart)
        .lte("paid_at", monthEnd);

      if (mpError) throw mpError;

      const membershipRevenue = (membershipPayments || []).reduce(
        (sum: number, p: any) => sum + convertRevenue(Number(p.gross_amount || 0), p.currency),
        0
      );

      // Calculate total revenue (appointments + invoices + walk-ins + memberships)
      const totalRevenue = monthlyRevenue + invoiceRevenue + walkInRevenue + membershipRevenue;

      setStats({
        monthlyAppointments: appointments?.length || 0,
        monthlyRevenue,
        invoiceRevenue,
        walkInRevenue,
        membershipRevenue,
        totalRevenue,
        projectedRevenue,
        downpaymentsReceived,
        newCustomers: newCustomersData?.length || 0,
        pendingAppointments: pendingCount,
        upcomingAppointments: (upcoming || []).map((a) => ({
          id: a.id,
          start_datetime: a.start_datetime,
          customer_name: a.customer?.name || "Unknown",
          service_name: a.service?.name || "Unknown",
          status: a.status,
        })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  }, [business, selectedLocationId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}
